import React, {useCallback} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {OverlayType} from 'modules/overlay/types'
import {ColorPath} from 'types/Theme'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {Tooltip, TooltipPlacement} from '@vio/atlas/Tooltip'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {noop} from '@vio/core/src/utils/function'

const StyledText = styled(Text)`
  display: flex;
  gap: ${cssTheme.layout.spacing.s100};
  align-items: center;
  cursor: pointer;
`

const Underlined = styled.span`
  text-decoration: underline;
  text-decoration-style: dotted;
`

const IconWrapper = styled.span`
  margin: 0 ${cssTheme.layout.spacing.s100};
`

interface Props {
  variant?: 'default' | 'perk'
  colorPath?: ColorPath
}

export const RoomSwitchRequiredTooltip = ({
  variant = 'default',
  colorPath = 'content.neutral.primary'
}: Props) => {
  const {formatMessage} = useIntl()
  const {isDesktop} = useDeviceLayout()
  const showOverlay = useShowOverlay()
  const isPerk = variant === 'perk'

  const onClick: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation()
      showOverlay({
        type: OverlayType.SplitBookingInfoBottomSheet
      })
    },
    [showOverlay]
  )

  const label = (
    <Box paddingTop={isPerk ? 's000' : isDesktop ? 's200' : 's100'}>
      <Box
        alignItems={isPerk ? 'center' : undefined}
        display="inline-flex"
        gap={isPerk ? 's250' : undefined}
        onClick={isDesktop ? noop : onClick}
      >
        {isPerk && (
          <Icon name="Refresh3" size="sm" colorPath="content.neutral.primary" />
        )}
        <StyledText variant={isPerk ? 'bodyS' : 'bodyXS'} colorPath={colorPath}>
          <Underlined data-chromatic="ignore">
            <FormattedMessageWrapper
              id="roomSwitchRequired"
              defaultMessage="Room switch required"
            />
          </Underlined>
        </StyledText>
        {isPerk ? (
          <Icon name="Information" size="sm" />
        ) : (
          <IconWrapper>
            <Icon name="Information" size="sm" />
          </IconWrapper>
        )}
      </Box>
    </Box>
  )

  if (isDesktop) {
    return (
      <Tooltip
        placement={TooltipPlacement.BottomEnd}
        title={formatMessage({
          id: 'switchRoomsAndSave',
          defaultMessage: 'Switch rooms and save'
        })}
        content={
          <Box maxWidth="232px">
            <FormattedMessageWrapper
              id="splitBooking.roomSwitchRequiredTooltipContent"
              defaultMessage="The cheapest way to stay here is to split your stay across two separate bookings."
            />
          </Box>
        }
      >
        {label}
      </Tooltip>
    )
  }

  return label
}
